import { useCallback, useEffect, useRef, useState } from 'react'

export interface DiagramClock {
    /** Current position on the timeline, in seconds. */
    t: number
    playing: boolean
    /** Play from the current position. Clears any pending hold. */
    play: () => void
    pause: () => void
    /** Jump instantly, paused. Clears any pending hold. */
    seekTo: (seconds: number) => void
    /** Play from the current position and STOP exactly at `stop`. Used by step mode's Next. */
    playUntil: (stop: number) => void
}

/**
 * The whole playback engine for a timeline-driven diagram: one accumulator, driven by
 * requestAnimationFrame, exposing a position in seconds.
 *
 * Deliberately not a port of the prototype's composition engine. Everything the diagram draws is
 * already a pure function of the clock, so the engine has nothing to do beyond advancing a
 * number, holding it, and looping it.
 *
 * Two details that are not obvious:
 *
 *   - The per-frame delta is clamped to 250ms. rAF stops firing in a background tab, so without
 *     the clamp, returning to the tab delivers one enormous frame and the diagram teleports past
 *     several scenes. Clamping makes a backgrounded tab pause rather than fast-forward.
 *
 *   - `playUntil` holds instead of looping. The loop is suppressed whenever a stop is pending,
 *     so Next can target a beat without the clock wrapping underneath it.
 */
export function useDiagramClock(duration: number, loop = true): DiagramClock {
    const [t, setT] = useState(0)
    const [playing, setPlaying] = useState(false)
    const timeRef = useRef(0)
    const stopAtRef = useRef<number | null>(null)

    const commit = useCallback((next: number) => {
        timeRef.current = next
        setT(next)
    }, [])

    useEffect(() => {
        if (!playing) return
        let raf = 0
        let last = performance.now()

        const step = (now: number) => {
            const dt = Math.min(0.25, (now - last) / 1000)
            last = now
            let next = timeRef.current + dt
            const stop = stopAtRef.current

            if (stop != null && next >= stop) {
                stopAtRef.current = null
                commit(stop)
                setPlaying(false)
                return
            }
            if (next >= duration) {
                if (loop && stop == null) {
                    next -= duration
                } else {
                    commit(duration)
                    setPlaying(false)
                    return
                }
            }
            commit(next)
            raf = requestAnimationFrame(step)
        }

        raf = requestAnimationFrame(step)
        return () => cancelAnimationFrame(raf)
    }, [playing, duration, loop, commit])

    const play = useCallback(() => {
        stopAtRef.current = null
        setPlaying(true)
    }, [])

    const pause = useCallback(() => setPlaying(false), [])

    const seekTo = useCallback(
        (seconds: number) => {
            stopAtRef.current = null
            commit(Math.max(0, Math.min(duration, seconds)))
            setPlaying(false)
        },
        [commit, duration]
    )

    const playUntil = useCallback(
        (stop: number) => {
            // Already at or past the target: land on it rather than playing a full lap to reach it.
            if (stop <= timeRef.current) {
                stopAtRef.current = null
                commit(stop)
                setPlaying(false)
                return
            }
            stopAtRef.current = stop
            setPlaying(true)
        },
        [commit]
    )

    return { t, playing, play, pause, seekTo, playUntil }
}

export default useDiagramClock
